// Pure index over the rows GET /data-availability returns: which delivery days exist for which
// table. Rows come from the network, so everything is validated and nothing is trusted.
// No React, router or Firebase imports.
import { normalizeCountry } from '../config/countries';
import type { CountryCode } from '../config/countries';
import { isIsoDay, parseBucket } from './dateWindow';
import type { IsoDay } from './dateWindow';

export type { IsoDay };

export interface AvailabilityRow {
  country_code: string;
  dataset: string;
  table_name: string;
  batch_date: string;
  total_rows: number;
  last_modified_time: string | null;
}

export interface AvailabilityIndex {
  country: CountryCode;
  /** table_name -> delivered days, ascending and de-duplicated. */
  tables: ReadonlyMap<string, readonly IsoDay[]>;
  /** Latest last_modified_time across valid rows, as an ISO 8601 UTC string. */
  lastModified: string | null;
  /** Date.now() when the rows were indexed. */
  fetchedAt: number;
}

const NO_DAYS: readonly IsoDay[] = Object.freeze([]) as readonly IsoDay[];

/** The BigQuery client serialises DATE and TIMESTAMP as { value }; accept that shape too. */
function unwrap(v: unknown): unknown {
  return v !== null && typeof v === 'object' && 'value' in v ? (v as { value: unknown }).value : v;
}

/**
 * Index the rows for one country. A row is kept only if its country_code normalises to `country`,
 * table_name is a non-empty string, batch_date is a real 'YYYY-MM-DD' day and total_rows is above
 * zero. Every other row is counted in `dropped`. Repeated (table, day) pairs collapse into one;
 * they are not counted as dropped. Table names the registry does not know are kept.
 */
export function indexAvailability(
  country: CountryCode,
  rows: readonly unknown[],
  now: number,
): { index: AvailabilityIndex; dropped: number } {
  const days = new Map<string, Set<IsoDay>>();
  let dropped = 0;
  let lastModifiedMs: number | null = null;

  for (const row of rows) {
    const r = row !== null && typeof row === 'object' ? (row as Record<string, unknown>) : null;
    const table = r?.table_name;
    const day = r ? unwrap(r.batch_date) : undefined;
    const totalRows = r ? Number(r.total_rows) : NaN;
    if (
      !r ||
      normalizeCountry(r.country_code) !== country ||
      typeof table !== 'string' ||
      table.trim() === '' ||
      !isIsoDay(day) ||
      !(Number.isFinite(totalRows) && totalRows > 0)
    ) {
      dropped += 1;
      continue;
    }
    let set = days.get(table);
    if (!set) {
      set = new Set();
      days.set(table, set);
    }
    set.add(day);
    const modified = parseBucket(r.last_modified_time);
    if (modified !== null && (lastModifiedMs === null || modified > lastModifiedMs)) lastModifiedMs = modified;
  }

  const tables = new Map<string, readonly IsoDay[]>();
  days.forEach((set, table) => tables.set(table, Array.from(set).sort()));

  return {
    index: {
      country,
      tables,
      lastModified: lastModifiedMs === null ? null : new Date(lastModifiedMs).toISOString(),
      fetchedAt: now,
    },
    dropped,
  };
}

/** Delivered days for a table; [] for an unknown table or none given. The empty array is a shared constant. */
export function datesFor(index: AvailabilityIndex, table: string | undefined): readonly IsoDay[] {
  if (table === undefined) return NO_DAYS;
  return index.tables.get(table) ?? NO_DAYS;
}

/** The most recent day, or null when there are none. */
export function latest(days: readonly IsoDay[]): IsoDay | null {
  let best: IsoDay | null = null;
  for (const d of days) if (best === null || d > best) best = d;
  return best;
}

/** `preferred` if it is one of `days`, otherwise the latest day (null for an empty list). */
export function pickDay(days: readonly IsoDay[], preferred?: IsoDay | null): IsoDay | null {
  if (preferred && days.includes(preferred)) return preferred;
  return latest(days);
}
